import { capitalize, smushName } from './string';

class BaseField {
    constructor(name, opts) {
        this.opts = opts || {};
        this.name = name;
        this.label = this.opts.label || capitalize(name);
    }

    contributeToClass() {}
    init() {}
    serialize() {}
}

class ValueField extends BaseField {
    constructor(name, opts) {
        super(name, opts);
        this.internalName = this.opts.internalName || '_' + name;
        this.eventName = this.opts.eventName || smushName('change', name);
        this.default = 'default' in this.opts ? this.opts.default : null;
    }

    contributeToClass(cls) {
        Object.defineProperty(cls.prototype, this.name, this.descriptor);
    }

    init(instance, data) {
        instance[this.internalName] =
            this.name in data ? this.unpack(data[this.name]) : this.default;
    }

    validate() {
        return true;
    }

    serialize(instance, data) {
        data[this.name] = this.pack(instance[this.name]);
    }

    pack(val) {
        return val;
    }
    unpack(val) {
        return val;
    }

    get descriptor() {
        let field = this;
        return {
            enumerable: true,
            get() {
                return this[field.internalName];
            },
            set(newVal) {
                if (!field.validate(newVal)) return;

                this[field.internalName] = newVal;
                this.emit(field.eventName, newVal);
            },
        };
    }

    propagateUpdate(container, myKey, path, val, eventPathPrefix, eventData) {
        if (path.length) {
            throw 'ValueField.deepUpdate expected empty path, got: ' + path;
        }
        if (this.validate(val)) {
            container[myKey] = val;
            eventData.push([...eventPathPrefix, val]);
        }
    }

    deepUpdate(instance, path, val) {
        let eventData = [];
        this.propagateUpdate(
            instance,
            this.internalName,
            path,
            val,
            [],
            eventData,
        );
        eventData.forEach((eventArgs) => {
            instance.emit(this.eventName, ...eventArgs);
        });
    }
}

class IntegerField extends ValueField {
    constructor(name, opts) {
        super(name, opts);
        this.min = 'min' in this.opts ? this.opts.min : null;
        this.max = 'max' in this.opts ? this.opts.max : null;
    }
    validate(newVal) {
        if (this.min !== null && newVal < this.min) return false;
        if (this.max !== null && newVal > this.max) return false;
        return true;
    }
}

class BooleanField extends ValueField {
    pack(val) {
        return val ? 1 : 0;
    }
    unpack(val) {
        return !!val;
    }
}

class LazyModelListField extends BaseField {
    constructor(name, model, opts) {
        super(name, opts);
        this.model = model;
        this.elementName = this.opts.elementName || this.name;
        this.getterName =
            this.opts.getterName || smushName('get', this.elementName);
        this.startIndex = this.opts.startIndex || 0;
        this.endIndex = 'endIndex' in this.opts ? this.opts.endIndex : null;
        this.defaultData = this.opts.defaultData || [];
    }

    contributeToClass(cls) {
        let field = this;

        cls.prototype[this.getterName] = function (i) {
            return field._getOrCreateItem(this[field.name], i);
        };
    }

    _getOrCreateItem(items, index) {
        if (index < this.startIndex) return null;
        if (this.endIndex !== null && index >= this.endIndex) return null;
        let item = items[index];
        if (!item) {
            item = new this.model();
            items[index] = item;
        }
        return item;
    }

    init(instance, data) {
        let items = [];
        let initialData = data[this.name] || this.defaultData;
        if (initialData) {
            let dataLen = initialData.length;
            if (this.endIndex !== null) {
                dataLen = Math.min(dataLen, this.endIndex - this.startIndex);
            }
            for (let i = 0; i < dataLen; i++) {
                items[i + this.startIndex] = new this.model(initialData[i]);
            }
        }
        instance[this.name] = items;
    }

    serialize(instance, data) {
        let itemData = [];
        for (var i = this.startIndex; i < instance[this.name].length; i++) {
            itemData.push(instance[this.getterName](i).serialize());
        }
        data[this.name] = itemData;
    }

    propagateUpdate(container, myKey, path, items) {
        if (path.length) {
            let [index, ...subpath] = path;
            let instance = this._getOrCreateItem(container[myKey], index);
            if (instance) instance.deepUpdate(subpath, items);
        } else {
            for (let [index, value] of Object.entries(items)) {
                let instance = this._getOrCreateItem(container[myKey], index);
                if (instance) instance.deepUpdate([], value);
            }
        }
    }

    deepUpdate(instance, path, val) {
        let eventData = [];
        this.propagateUpdate(instance, this.name, path, val, [], eventData);
        // ignore eventData, as events are handled by the models
    }
}

class BaseStructField extends BaseField {
    /* common superclass of StructField (name-indexed) and TupleField (number-indexed) */
    constructor(name, fields, opts) {
        super(name, opts);

        this.fieldList = fields;

        this.getterName = this.opts.getterName || smushName('get', name);
        this.setterName = this.opts.setterName || smushName('set', name);
        this.eventName = this.opts.eventName || smushName('change', name);
    }

    contributeToClass(cls) {
        let field = this;

        cls.prototype[this.getterName] = function (...keys) {
            if (keys.length) {
                /* return a single element */
                return field.get(this[field.name], keys);
            } else {
                /* return the complete structure */
                return this[field.name];
            }
        };

        cls.prototype[this.setterName] = function (...args) {
            let val = args.pop();
            if (field.set(this[field.name], args, val)) {
                this.emit(field.eventName, ...args, val);
            }
        };
    }

    get(obj, keys) {
        let key, rest;
        [key, ...rest] = keys;
        let val = obj[key];
        if (rest.length) {
            return this.fields[key].get(val, rest);
        } else {
            return val;
        }
    }

    set(obj, keys, val) {
        let key, rest;
        [key, ...rest] = keys;
        let field = this.fields[key];
        if (rest.length) {
            return field.set(obj[key], rest, val);
        } else {
            if (!field.validate(val)) return false;
            obj[key] = val;
            return true;
        }
    }

    init(instance, data) {
        if (this.name in data) {
            instance[this.name] = this.unpack(data[this.name]);
        } else {
            instance[this.name] = this.default;
        }
    }
    serialize(instance, data) {
        data[this.name] = this.pack(instance[this.name]);
    }

    propagateUpdate(container, myKey, path, items, eventPathPrefix, eventData) {
        if (path.length) {
            let [key, ...subpath] = path;
            let field = this.fields[key];
            if (field) {
                field.propagateUpdate(
                    container[myKey],
                    key,
                    subpath,
                    items,
                    [...eventPathPrefix, key],
                    eventData,
                );
            }
        } else {
            for (let [key, value] of Object.entries(items)) {
                let field = this.fields[key];
                if (field) {
                    field.propagateUpdate(
                        container[myKey],
                        key,
                        [],
                        value,
                        [...eventPathPrefix, key],
                        eventData,
                    );
                }
            }
        }
    }

    deepUpdate(instance, path, val) {
        let eventData = [];
        this.propagateUpdate(instance, this.name, path, val, [], eventData);
        eventData.forEach((eventArgs) => {
            instance.emit(this.eventName, ...eventArgs);
        });
    }
}

class StructField extends BaseStructField {
    constructor(name, fields, opts) {
        super(name, fields, opts);

        this.fields = {};
        fields.forEach((field) => {
            this.fields[field.name] = field;
        });
    }

    get default() {
        let result = {};
        this.fieldList.forEach((field) => {
            result[field.name] = field.default;
        });
        return result;
    }

    // TODO: option to (de)serialize as either list or dict
    unpack(val) {
        let result = {};
        this.fieldList.forEach((field, i) => {
            result[field.name] = field.unpack(val[i]);
        });
        return result;
    }
    pack(val) {
        return this.fieldList.map((field) => {
            return field.pack(val[field.name]);
        });
    }
}

class TupleField extends BaseStructField {
    constructor(name, fields, opts) {
        super(name, fields, opts);
        this.fields = fields;
    }

    get default() {
        return this.fieldList.map((field) => {
            return field.default;
        });
    }

    unpack(val) {
        return this.fieldList.map((field, i) => {
            return field.unpack(val[i]);
        });
    }
    pack(val) {
        return this.fieldList.map((field, i) => {
            return field.pack(val[i]);
        });
    }
}

class LazyListField extends BaseField {
    constructor(name, subfield, opts) {
        super(name, opts);

        this.subfield = subfield;
        this.elementName = this.opts.elementName || this.subfield.name;
        this.getterName =
            this.opts.getterName || smushName('get', this.elementName);
        this.setterName =
            this.opts.setterName || smushName('set', this.elementName);
        this.eventName =
            this.opts.eventName || smushName('change', this.elementName);
        this.startIndex = this.opts.startIndex || 0;
        if ('endIndex' in this.opts) {
            this.endIndex = this.opts.endIndex;
            this.length = this.endIndex - this.startIndex;
        } else if ('length' in this.opts) {
            this.length = this.opts.length;
            this.endIndex = this.startIndex + this.length;
        } else {
            this.length = this.endIndex = null;
        }
        this.default = [];
    }

    contributeToClass(cls) {
        let field = this;

        cls.prototype[this.getterName] = function (...keys) {
            if (keys.length) {
                /* return a list item */
                return field.get(this[field.name], keys);
            } else {
                /* return the list */
                return this[field.name];
            }
        };

        cls.prototype[this.setterName] = function (...args) {
            let val = args.pop();
            if (field.set(this[field.name], args, val)) {
                this.emit(field.eventName, ...args, val);
            }
        };
    }

    _indexIsValid(index) {
        return (
            index >= this.startIndex &&
            (this.endIndex === null || index < this.endIndex)
        );
    }
    _getOrCreateItem(obj, key) {
        let val = obj[key];
        if (val === undefined) {
            val = obj[key] = this.subfield.default;
        }
        return val;
    }

    get(obj, keys) {
        let key, rest;
        [key, ...rest] = keys;
        if (!this._indexIsValid(key)) return null;
        let val = this._getOrCreateItem(obj, key);
        if (rest.length) {
            return this.subfield.get(val, rest);
        } else {
            return val;
        }
    }

    set(obj, keys, val) {
        let key, rest;
        [key, ...rest] = keys;
        if (!this._indexIsValid(key)) return false;
        if (rest.length) {
            return this.subfield.set(
                this._getOrCreateItem(obj, key),
                rest,
                val,
            );
        } else {
            if (!this.subfield.validate(val)) return false;
            obj[key] = val;
            return true;
        }
    }

    unpack(val) {
        let items = [];
        val.forEach((itemData, i) => {
            items[i + this.startIndex] = this.subfield.unpack(itemData);
        });
        return items;
    }
    pack(val) {
        let itemData = [];
        for (var i = this.startIndex; i < val.length; i++) {
            itemData.push(this.subfield.pack(this.get(val, [i])));
        }
        return itemData;
    }

    init(instance, data) {
        if (this.name in data) {
            instance[this.name] = this.unpack(data[this.name]);
        } else {
            instance[this.name] = this.default;
        }
    }
    serialize(instance, data) {
        data[this.name] = this.pack(instance[this.name]);
    }

    propagateUpdate(container, myKey, path, items, eventPathPrefix, eventData) {
        if (path.length) {
            let [index, ...subpath] = path;
            if (this._indexIsValid(index)) {
                this._getOrCreateItem(container[myKey], index);
                this.subfield.propagateUpdate(
                    container[myKey],
                    index,
                    subpath,
                    items,
                    [...eventPathPrefix, index],
                    eventData,
                );
            }
        } else {
            for (let [index, value] of Object.entries(items)) {
                if (this._indexIsValid(index)) {
                    this._getOrCreateItem(container[myKey], index);
                    this.subfield.propagateUpdate(
                        container[myKey],
                        index,
                        [],
                        value,
                        [...eventPathPrefix, index],
                        eventData,
                    );
                }
            }
        }
    }

    deepUpdate(instance, path, val) {
        let eventData = [];
        this.propagateUpdate(instance, this.name, path, val, [], eventData);
        eventData.forEach((eventArgs) => {
            instance.emit(this.eventName, ...eventArgs);
        });
    }
}

class ListField extends BaseField {
    constructor(name, subfield, opts) {
        super(name, opts);

        this.subfield = subfield;
        this.elementName = this.opts.elementName || this.subfield.name;
        this.getterName =
            this.opts.getterName || smushName('get', this.elementName);
        this.setterName =
            this.opts.setterName || smushName('set', this.elementName);
        this.eventName =
            this.opts.eventName || smushName('change', this.elementName);
        this.startIndex = this.opts.startIndex || 0;
        if ('endIndex' in this.opts) {
            this.endIndex = this.opts.endIndex;
            this.length = this.endIndex - this.startIndex;
        } else if ('length' in this.opts) {
            this.length = this.opts.length;
            this.endIndex = this.startIndex + this.length;
        } else {
            throw 'Either endIndex or length must be provided';
        }
    }

    get default() {
        let result = [];
        for (let i = this.startIndex; i < this.endIndex; i++) {
            result[i] = this.subfield.default;
        }
        return result;
    }

    contributeToClass(cls) {
        let field = this;

        cls.prototype[this.getterName] = function (...keys) {
            if (keys.length) {
                /* return a list item */
                return field.get(this[field.name], keys);
            } else {
                /* return the list */
                return this[field.name];
            }
        };

        cls.prototype[this.setterName] = function (...args) {
            let val = args.pop();
            if (field.set(this[field.name], args, val)) {
                this.emit(field.eventName, ...args, val);
            }
        };
    }

    _indexIsValid(index) {
        return (
            index >= this.startIndex &&
            (this.endIndex === null || index < this.endIndex)
        );
    }

    get(obj, keys) {
        let key, rest;
        [key, ...rest] = keys;
        if (!this._indexIsValid(key)) return null;
        if (rest.length) {
            return this.subfield.get(obj[key], rest);
        } else {
            return obj[key];
        }
    }

    set(obj, keys, val) {
        let key, rest;
        [key, ...rest] = keys;
        if (!this._indexIsValid(key)) return false;
        if (rest.length) {
            return this.subfield.set(this.get(obj, [key]), rest, val);
        } else {
            if (!this.subfield.validate(val)) return false;
            obj[key] = val;
            return true;
        }
    }

    unpack(val) {
        let items = [];
        val.forEach((itemData, i) => {
            items[i + this.startIndex] = this.subfield.unpack(itemData);
        });
        return items;
    }
    pack(val) {
        let itemData = [];
        for (var i = this.startIndex; i < val.length; i++) {
            itemData.push(this.subfield.pack(val[i]));
        }
        return itemData;
    }

    init(instance, data) {
        if (this.name in data) {
            instance[this.name] = this.unpack(data[this.name]);
        } else {
            instance[this.name] = this.default;
        }
    }
    serialize(instance, data) {
        data[this.name] = this.pack(instance[this.name]);
    }

    propagateUpdate(container, myKey, path, items, eventPathPrefix, eventData) {
        if (path.length) {
            let [index, ...subpath] = path;
            if (this._indexIsValid(index)) {
                this.subfield.propagateUpdate(
                    container[myKey],
                    index,
                    subpath,
                    items,
                    [...eventPathPrefix, index],
                    eventData,
                );
            }
        } else {
            for (let [index, value] of Object.entries(items)) {
                if (this._indexIsValid(index)) {
                    this.subfield.propagateUpdate(
                        container[myKey],
                        index,
                        [],
                        value,
                        [...eventPathPrefix, index],
                        eventData,
                    );
                }
            }
        }
    }

    deepUpdate(instance, path, val) {
        let eventData = [];
        this.propagateUpdate(instance, this.name, path, val, [], eventData);
        eventData.forEach((eventArgs) => {
            instance.emit(this.eventName, ...eventArgs);
        });
    }
}

export {
    ValueField,
    IntegerField,
    BooleanField,
    LazyModelListField,
    StructField,
    TupleField,
    LazyListField,
    ListField,
};
